const TERMINAL_CLARIFICATION_STATUSES = new Set([
  "resolved",
  "cleared",
  "cancelled",
  "new_thread",
]);

const isPlainObject = (value) =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const pickObject = (...values) => values.find((value) => isPlainObject(value)) || null;

const pickStatus = (...values) => {
  const raw = values.find((value) => value !== null && value !== undefined && String(value).trim() !== "");
  return raw === undefined ? "" : String(raw).trim().toLowerCase();
};

export const normalizeClarificationFields = (value = {}) => {
  if (!isPlainObject(value)) {
    return {
      requiresClarification: false,
      pendingClarificationStatus: "",
      clarificationRequest: null,
      pendingClarification: null,
      clarificationResolution: null,
      resolvedClarificationContext: null,
    };
  }

  return {
    requiresClarification: Boolean(
      value.requiresClarification ?? value.requires_clarification ?? false
    ),
    pendingClarificationStatus: pickStatus(
      value.pendingClarificationStatus,
      value.pending_clarification_status
    ),
    clarificationRequest: pickObject(
      value.clarificationRequest,
      value.clarification_request
    ),
    pendingClarification: pickObject(
      value.pendingClarification,
      value.pending_clarification
    ),
    clarificationResolution: pickObject(
      value.clarificationResolution,
      value.clarification_resolution
    ),
    resolvedClarificationContext: pickObject(
      value.resolvedClarificationContext,
      value.resolved_clarification_context
    ),
  };
};

export const deriveActiveClarification = (messages = []) => {
  let activeClarification = null;

  messages.forEach((message) => {
    if (message?.role !== "bot") return;

    const clarification = normalizeClarificationFields(message);

    if (
      clarification.requiresClarification &&
      clarification.pendingClarificationStatus === "pending" &&
      clarification.clarificationRequest
    ) {
      activeClarification = {
        messageId: message.id,
        messageDbId: message.messageId ?? null,
        responseId: message.responseId ?? null,
        question:
          clarification.clarificationRequest.question ||
          clarification.pendingClarification?.clarification_question ||
          "",
        ...clarification,
      };
      return;
    }

    if (TERMINAL_CLARIFICATION_STATUSES.has(clarification.pendingClarificationStatus)) {
      activeClarification = null;
    }
  });

  return activeClarification;
};

export const buildQueryPayload = ({
  mode = "normal",
  sessionId,
  sourceDbConfigId,
  responseId,
  messageText = "",
  selectedOption = null,
  dbMessageId = null,
  primaryModel = null,
  faq = null,
}) => {
  const payload = {
    session_id: sessionId,
    source_db_config_id: sourceDbConfigId,
    response_id: responseId,
  };
  const selectedPrimaryModel = String(primaryModel || "").trim();
  if (selectedPrimaryModel) {
    payload.primary_model = selectedPrimaryModel;
  }

  if (faq) {
    payload.faq_query_id = faq.id;
    payload.faq_query_revision = faq.revision;
    return payload;
  }

  if (mode === "normal") {
    payload.question = messageText;
  } else if (mode === "selected_option") {
    payload.clarification_response = {
      type: "selected_option",
      selected_option: selectedOption,
    };
  } else if (mode === "custom") {
    payload.clarification_response = {
      type: "custom",
      custom_text: messageText,
    };
  } else if (mode === "dismiss") {
    payload.clarification_response = {
      type: "dismiss",
    };
  } else {
    throw new Error(`Unsupported query payload mode: ${mode}`);
  }

  if (dbMessageId) {
    payload.edit_message_id = dbMessageId;
  }

  return payload;
};

export const shapeAssistantClarificationFields = (value = {}) =>
  normalizeClarificationFields(value);
